import { Component, HostListener, inject, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription, tap } from 'rxjs';
import { LoggerService } from '../../../services/logger/logger.service';
import { resetGrantRevokePermissionState } from '../../../store/actions/grant-revoke-permissions.action';
import { ContentUtil } from '../../../utils/content/content.util';
import { Content } from 'src/common/interfaces/content.interface';
import { ErrorPage } from '../../../services/generic-error/error-page-utils';
import { ErrorService } from '../../../services/generic-error/error.service';
import {
    selectOperationResponse,
    selectSuccessfulAccountsCount,
    selectUniquePin,
    selectAddUserStepperSelectedAccess,
    selectAddUserStepperSelectedUserType,
} from '../../../store/selectors/grant-revoke-permissions.selector';
import { selectUserInfo } from '../../../store/selectors/account-permissions.selector';
import { PageComponent } from '../page.component';
import { PagesOfAddUserToOneSelectedAccount } from '../enums/pages-of-add-user-to-one-selected-account.enum';
import { FlowKey } from '../../../enums/flow-keys.enum';
import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import { StepperComponent } from '../../stepper/stepper.component';
import { GafConfirmationPageMvpComponent } from '../gaf-confirmation-page-mvp/gaf-confirmation-page-component-mvp';
import { OperationResponse } from '../../../interfaces/access-flow-stepper.interface';
import { BulkGrantPermissionResponseDTO } from '../../../../common/dtos/access-permissions.dto';

@Component({
    selector: 'zci-add-user-to-account-page-mvp',
    templateUrl: './add-user-to-account-page-mvp.component.html',
    styleUrls: ['./add-user-to-account-page-mvp.component.scss'],
    imports: [AsyncPipe, NgClass, NgIf, StepperComponent, GafConfirmationPageMvpComponent],
})
export class AddUserToAccountPageMvpComponent extends PageComponent implements OnDestroy {
    content: Content = ContentUtil.content;
    forceSurveyZeroHeight: boolean = true;
    currentPage: PagesOfAddUserToOneSelectedAccount = PagesOfAddUserToOneSelectedAccount.STEPPER;
    pagesOfAddUserToOneSelectedAccount = PagesOfAddUserToOneSelectedAccount;
    FlowKeyEnum = FlowKey;
    private readonly store = inject(Store);
    selectUniquePin$: Observable<string> = this.store.select(selectUniquePin).pipe(
        tap((value) => {
            if (value) {
                setTimeout(() => {
                    this.updatePageView();
                }, 1000);
            }
        }),
    );
    private logger = inject(LoggerService);
    private window = inject(Window);
    private errorService = inject(ErrorService);
    selectOperationResponse$: Observable<OperationResponse> = this.store.select(selectOperationResponse);

    subs = new Subscription();

    constructor() {
        super();
        this.resetGrantRevokePermissionState();
        this.handleOperationResponse();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    handleOperationResponse(): void {
        this.subs.add(
            combineLatest([
                this.store.select(selectOperationResponse),
                this.store.select(selectSuccessfulAccountsCount),
                this.store.select(selectUserInfo),
                this.store.select(selectAddUserStepperSelectedAccess),
                this.store.select(selectAddUserStepperSelectedUserType),
            ])
            .subscribe(([operationResponse, successCount, grantorInfo, accessLevel, userType]) => {
                    if (operationResponse) {
                        const results = (operationResponse as BulkGrantPermissionResponseDTO).results;
                        const totalCount = results?.length ?? 0;
                        const failedCount = results?.filter((r) => r.confirmationNumber === '').length ?? 0;
                        const hasPartialFailure = results?.some((r) => r.confirmationNumber === '');

                        if (successCount === 0) {
                            this.logger.info(
                                `[AddUserToAccountPageMvp] Grantor POID: ${grantorInfo?.clientPoid}, ` +
                                `Navigation to GAF_SUBMIT_ALL_FAILED page, ` +
                                `Total results: ${totalCount}, Successful: ${successCount}, Failed: ${failedCount}, ` +
                                `Access level: ${accessLevel}, Grantee type: ${userType}`,
                            );
                            this.errorService.showError(ErrorPage.GAF_SUBMIT_ALL_FAILED);
                        } else if (hasPartialFailure) {
                            this.logger.info(
                                `[AddUserToAccountPageMvp] Grantor POID: ${grantorInfo?.clientPoid}, ` +
                                `Navigation to GAF_PARTIAL_SUCCESS page, ` +
                                `Total results: ${totalCount}, Successful: ${successCount}, Failed: ${failedCount}, ` +
                                `Access level: ${accessLevel}, Grantee type: ${userType}`,
                            );
                            this.errorService.showError(ErrorPage.GAF_PARTIAL_SUCCESS);
                            return;
                        }

                        this.logger.info(
                            `[AddUserToAccountPageMvp] Grantor POID: ${grantorInfo?.clientPoid}, ` +
                            `Navigating to GAF_ALL_SUCCESS page, ` +
                            `Access level: ${accessLevel}, Grantee type: ${userType}`,
                        );

                        setTimeout(() => {
                            this.updatePageView();
                        }, 1000);                
                    }
                }),
        );
    }

    @HostListener('window:MDigital_Form_Displayed')
    removeForcedHeightZero() {
        this.forceSurveyZeroHeight = false;
    }

    updatePageView(): void {
        const nativeWindow: Window | any = this.window;
        if (
            nativeWindow?.KAMPYLE_ONSITE_SDK &&
            typeof nativeWindow.KAMPYLE_ONSITE_SDK?.updatePageView === 'function'
        ) {
            nativeWindow.KAMPYLE_ONSITE_SDK.updatePageView();
        } else {
            this.logger.warn('Failed to load Medallia SDK');
        }
    }

    changeCurrentPage(page: PagesOfAddUserToOneSelectedAccount) {
        this.currentPage = page;
    }

    resetGrantRevokePermissionState(): void {
        this.store.dispatch(resetGrantRevokePermissionState());
    }
}
